/**
 * /api-spend — Dedicated LLM / API spend dashboard.
 *
 * Sister page to /settings/api-costs:
 *   - /settings/api-costs is where you CONFIGURE accounts + thresholds
 *   - /api-spend is where you GLANCE — KPI tiles, per-account sparklines,
 *     active alerts, total runway view.
 *
 * Auto-refreshes every 60s. Reads:
 *   GET /api/vendor-watch/accounts            → list with snapshot + alerts
 *   GET /api/vendor-watch/accounts/{id}/snapshots?limit=30  → sparkline data
 *   GET /api/vendor-watch/alerts              → all active alerts
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SettingsIcon from "@mui/icons-material/Settings";

import { api, errorMessage } from "@/lib/api";

// ─── types (subset) ───────────────────────────────────────────────────────

type AlertOut = {
  alert_type: string;
  severity: "info" | "warning" | "critical";
  triggered_at: string;
  message: string;
};

type AutoRechargeView = {
  charges_this_month: number | null;
  days_per_charge: number | null;
  forecast_month_cents: number | null;
  variance_vs_budget_pct: number | null;
};

type SnapshotOut = {
  captured_at: string;
  period_start: string;
  period_end: string;
  amount_cents: number;
  daily_burn_cents: number;
  projected_days_to_zero: number | null;
  credit_balance_cents: number | null;
};

type AccountOut = {
  id: string;
  vendor: string;
  account_label: string;
  is_mission_critical: boolean;
  is_active: boolean;
  credit_balance_cents: number | null;
  monthly_budget_cents: number | null;
  auto_recharge_view: AutoRechargeView;
  last_synced_at: string | null;
  last_sync_status: "never" | "ok" | "error";
  latest_snapshot: SnapshotOut | null;
  active_alerts: AlertOut[];
};

// ─── helpers ──────────────────────────────────────────────────────────────

const dollars = (cents: number | null | undefined) => {
  if (cents == null) return "—";
  const v = cents / 100;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
};

const dollarsFull = (cents: number | null | undefined) =>
  cents == null ? "—" : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const pct = (v: number | null | undefined) =>
  v == null ? "" : `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`;

const ago = (iso: string | null) => {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ─── Sparkline component — inline SVG, no dependency ──────────────────────

function Sparkline({
  values,
  width = 120,
  height = 36,
  stroke = "#1976d2",
  fill,
}: {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
}) {
  if (!values.length) {
    return (
      <Box sx={{ width, height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" color="text.disabled">no data</Typography>
      </Box>
    );
  }
  if (values.length === 1) {
    // Single point — render as a dot
    return (
      <svg width={width} height={height}>
        <circle cx={width / 2} cy={height / 2} r="3" fill={stroke} />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPoints = `${points} ${width},${height} 0,${height}`;
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {fill && <polygon points={areaPoints} fill={fill} />}
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ─── route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/api-spend")({
  component: ApiSpendDashboard,
});

function ApiSpendDashboard() {
  const qc = useQueryClient();

  const accountsQuery = useQuery({
    queryKey: ["vendor-watch", "accounts"],
    queryFn: () => api<{ accounts: AccountOut[]; count: number }>("/api/vendor-watch/accounts"),
    refetchInterval: 60_000,
  });

  const accounts = accountsQuery.data?.accounts ?? [];

  // Fetch per-account snapshot history in parallel for sparklines
  const historyQueries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: ["vendor-watch", "accounts", a.id, "snapshots"],
      queryFn: () =>
        api<{ snapshots: SnapshotOut[] }>(`/api/vendor-watch/accounts/${a.id}/snapshots?limit=30`),
      refetchInterval: 60_000,
      enabled: !!a.id,
    })),
  });

  const syncAll = useMutation({
    mutationFn: () => api("/api/vendor-watch/sync-all", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor-watch"] }),
  });

  // Aggregates
  const totalMtdCents = accounts.reduce((s, a) => s + (a.latest_snapshot?.amount_cents ?? 0), 0);
  const totalForecastCents = accounts.reduce(
    (s, a) => s + (a.auto_recharge_view.forecast_month_cents ?? 0), 0,
  );
  const totalBudgetCents = accounts.reduce(
    (s, a) => s + (a.monthly_budget_cents ?? 0), 0,
  );
  const totalDailyBurnCents = accounts.reduce(
    (s, a) => s + (a.latest_snapshot?.daily_burn_cents ?? 0), 0,
  );
  const allAlerts: { account: string; critical: boolean; alert: AlertOut }[] = accounts.flatMap((a) =>
    a.active_alerts.map((al) => ({
      account: `${a.vendor}/${a.account_label}`,
      critical: a.is_mission_critical,
      alert: al,
    })),
  );
  const criticalCount = allAlerts.filter((x) => x.alert.severity === "critical").length;
  const missionCriticalAtRisk = accounts.filter(
    (a) => a.is_mission_critical && a.active_alerts.some((al) => al.severity === "critical"),
  ).length;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">LLM &amp; API Spend Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Live view of every monitored vendor account. Sparklines show the last 30 sync points;
            mission-critical accounts are marked. Auto-refresh every 60s.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<SyncIcon />}
            onClick={() => syncAll.mutate()}
            disabled={syncAll.isPending || accounts.length === 0}
            variant="outlined"
          >
            {syncAll.isPending ? "Syncing…" : "Sync all"}
          </Button>
          <Button
            component={Link}
            to="/settings/api-costs"
            startIcon={<SettingsIcon />}
            variant="text"
          >
            Configure
          </Button>
        </Stack>
      </Stack>

      {/* ── KPI strip ─────────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 3 }}>
        <KpiTile
          label="Total MTD spend"
          value={dollarsFull(totalMtdCents)}
          sub={`${dollarsFull(totalDailyBurnCents)}/day burn`}
        />
        <KpiTile
          label="Forecast for month"
          value={dollarsFull(totalForecastCents)}
          sub={
            totalBudgetCents > 0
              ? `Budget ${dollarsFull(totalBudgetCents)} · ${pct(
                  ((totalForecastCents - totalBudgetCents) / totalBudgetCents) * 100,
                )}`
              : "No budget set"
          }
          urgent={
            totalBudgetCents > 0 &&
            totalForecastCents > totalBudgetCents * 1.2
          }
        />
        <KpiTile
          label="Accounts monitored"
          value={String(accounts.length)}
          sub={
            accounts.filter((a) => a.is_mission_critical).length
              ? `${accounts.filter((a) => a.is_mission_critical).length} mission-critical`
              : "—"
          }
        />
        <KpiTile
          label="Active alerts"
          value={String(allAlerts.length)}
          sub={
            criticalCount > 0
              ? `⚠️ ${criticalCount} critical${missionCriticalAtRisk ? ` (${missionCriticalAtRisk} mission-critical)` : ""}`
              : allAlerts.length > 0
                ? `${allAlerts.filter((x) => x.alert.severity === "warning").length} warnings`
                : "All clear"
          }
          urgent={criticalCount > 0}
        />
      </Box>

      {/* ── Active alerts panel ──────────────────────────────────────── */}
      {allAlerts.length > 0 && (
        <Card sx={{ mb: 3, borderLeft: 4, borderColor: criticalCount > 0 ? "error.main" : "warning.main" }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              {criticalCount > 0 ? <ErrorOutlineIcon color="error" /> : <WarningAmberIcon color="warning" />}
              <Typography variant="subtitle1">
                {allAlerts.length} active alert{allAlerts.length === 1 ? "" : "s"}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {allAlerts.map((x, i) => (
                <Alert
                  key={i}
                  severity={x.alert.severity === "critical" ? "error" : x.alert.severity === "warning" ? "warning" : "info"}
                  sx={{ "& .MuiAlert-message": { width: "100%" } }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      <strong>
                        {x.account}
                        {x.critical && " ⚠️"}
                      </strong>{" "}
                      <Chip size="small" label={x.alert.alert_type} sx={{ ml: 0.5, fontSize: "0.65rem" }} />
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{x.alert.message}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                      {ago(x.alert.triggered_at)}
                    </Typography>
                  </Stack>
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Per-account cards ────────────────────────────────────────── */}
      {accountsQuery.isLoading ? (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              No vendor accounts monitored yet.
            </Typography>
            <Button component={Link} to="/settings/api-costs" variant="contained">
              Add your first account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 2 }}>
          {accounts.map((a, i) => {
            const hist = historyQueries[i]?.data?.snapshots ?? [];
            // Sort by captured_at ascending for sparkline left-to-right
            const sorted = [...hist].sort((x, y) => x.captured_at.localeCompare(y.captured_at));
            const mtdSeries = sorted.map((s) => s.amount_cents);
            const burnSeries = sorted.map((s) => s.daily_burn_cents);
            const variance = a.auto_recharge_view.variance_vs_budget_pct;
            const overBudget = variance != null && variance > 20;
            return (
              <Card key={a.id} sx={{
                borderLeft: 4,
                borderColor: a.is_mission_critical
                  ? a.active_alerts.some((al) => al.severity === "critical")
                    ? "error.main" : "warning.main"
                  : "divider",
              }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="subtitle1" component="span">
                          <strong>{a.vendor}</strong>/{a.account_label}
                        </Typography>
                        {a.is_mission_critical && (
                          <Chip size="small" color="error" label="MISSION-CRITICAL" sx={{ fontSize: "0.6rem", height: 18 }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Last sync {ago(a.last_synced_at)} · status: {a.last_sync_status}
                      </Typography>
                    </Box>
                    <Tooltip title="Open account settings">
                      <IconButton size="small" component={Link} to="/settings/api-costs">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {/* MTD + sparkline */}
                  <Stack direction="row" alignItems="flex-end" spacing={2} sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">MTD spend</Typography>
                      <Typography variant="h5">{dollarsFull(a.latest_snapshot?.amount_cents)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                      <Sparkline values={mtdSeries} width={140} height={40} stroke="#1976d2" fill="rgba(25,118,210,0.12)" />
                    </Box>
                  </Stack>

                  {/* Forecast vs budget */}
                  {a.monthly_budget_cents != null && a.monthly_budget_cents > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption">
                          Forecast {dollarsFull(a.auto_recharge_view.forecast_month_cents)} of {dollarsFull(a.monthly_budget_cents)}
                        </Typography>
                        <Typography variant="caption" color={overBudget ? "error.main" : "text.secondary"}>
                          {pct(variance)}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          ((a.auto_recharge_view.forecast_month_cents ?? 0) / a.monthly_budget_cents) * 100,
                          100,
                        )}
                        color={overBudget ? "error" : "primary"}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  )}

                  {/* Metric grid */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5 }}>
                    <Metric
                      label="Daily burn"
                      value={dollars(a.latest_snapshot?.daily_burn_cents)}
                    />
                    <Metric
                      label="Charges this mo"
                      value={
                        a.auto_recharge_view.charges_this_month != null
                          ? String(a.auto_recharge_view.charges_this_month)
                          : "—"
                      }
                    />
                    <Metric
                      label="Days to zero"
                      value={
                        a.latest_snapshot?.projected_days_to_zero != null
                          ? `${a.latest_snapshot.projected_days_to_zero.toFixed(0)}d`
                          : "—"
                      }
                      urgent={
                        a.latest_snapshot?.projected_days_to_zero != null &&
                        a.latest_snapshot.projected_days_to_zero < (a.is_mission_critical ? 14 : 7)
                      }
                    />
                  </Box>

                  {/* Burn rate sparkline */}
                  {burnSeries.length > 1 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Daily burn trend</Typography>
                      <Sparkline values={burnSeries} width={400} height={32} stroke="#ed6c02" />
                    </Box>
                  )}

                  {a.active_alerts.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      {a.active_alerts.map((al, j) => (
                        <Chip
                          key={j}
                          size="small"
                          color={al.severity === "critical" ? "error" : "warning"}
                          label={al.alert_type}
                          sx={{ mr: 0.5, fontSize: "0.65rem" }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {accountsQuery.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {errorMessage(accountsQuery.error)}
        </Alert>
      )}
    </Box>
  );
}

function KpiTile({
  label, value, sub, urgent,
}: { label: string; value: string; sub: string; urgent?: boolean }) {
  return (
    <Card sx={{ borderLeft: 4, borderColor: urgent ? "error.main" : "primary.main" }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" color={urgent ? "error.main" : "text.primary"} sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {sub}
        </Typography>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, urgent }: { label: string; value: string; urgent?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" color={urgent ? "error.main" : "text.primary"} fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}
